/**
 * テロップ用 Web フォントの読み込み (Remotion 専用)。
 *
 * Ported from video-editor repo (src/remotion/captionFontLoader.ts) — import パス以外は無改変。
 *
 * 許可リスト (../fonts/captionFonts.ts) の各フォントを @remotion/google-fonts で読み込む。
 * loadFont はフォント×ウェイト×サブセット単位で内部キャッシュされ、読み込み中は
 * delayRender でレンダリングを待たせるため、コンポーネントの描画中に毎回呼んでも安全
 * (Remotion 公式の使い方どおり)。使うフォントだけを必要になった時点で読み込む。
 *
 * 各モジュールの static import はメタデータと loadFont 関数だけで、フォントファイル自体は
 * loadFont を呼んだときに初めて取得される。
 */
import * as NotoSansJP from '@remotion/google-fonts/NotoSansJP';
import * as ZenKakuGothicNew from '@remotion/google-fonts/ZenKakuGothicNew';
import * as ZenKakuGothicAntique from '@remotion/google-fonts/ZenKakuGothicAntique';
import * as IBMPlexSansJP from '@remotion/google-fonts/IBMPlexSansJP';
import * as LINESeedJP from '@remotion/google-fonts/LINESeedJP';
import * as MPLUS2 from '@remotion/google-fonts/MPLUS2';
import * as MPLUS1p from '@remotion/google-fonts/MPLUS1p';
import * as MPLUSU from '@remotion/google-fonts/MPLUSU';
import * as MPLUS1Code from '@remotion/google-fonts/MPLUS1Code';
import * as BIZUDPGothic from '@remotion/google-fonts/BIZUDPGothic';
import * as SawarabiGothic from '@remotion/google-fonts/SawarabiGothic';
import * as Kosugi from '@remotion/google-fonts/Kosugi';
import * as NotoSerifJP from '@remotion/google-fonts/NotoSerifJP';
import * as ShipporiMincho from '@remotion/google-fonts/ShipporiMincho';
import * as ShipporiMinchoB1 from '@remotion/google-fonts/ShipporiMinchoB1';
import * as ZenOldMincho from '@remotion/google-fonts/ZenOldMincho';
import * as ZenAntique from '@remotion/google-fonts/ZenAntique';
import * as ZenAntiqueSoft from '@remotion/google-fonts/ZenAntiqueSoft';
import * as ShipporiAntique from '@remotion/google-fonts/ShipporiAntique';
import * as ShipporiAntiqueB1 from '@remotion/google-fonts/ShipporiAntiqueB1';
import * as BIZUDPMincho from '@remotion/google-fonts/BIZUDPMincho';
import * as SawarabiMincho from '@remotion/google-fonts/SawarabiMincho';
import * as HinaMincho from '@remotion/google-fonts/HinaMincho';
import * as NewTegomin from '@remotion/google-fonts/NewTegomin';
import * as MPLUSRounded1c from '@remotion/google-fonts/MPLUSRounded1c';
import * as ZenMaruGothic from '@remotion/google-fonts/ZenMaruGothic';
import * as KosugiMaru from '@remotion/google-fonts/KosugiMaru';
import * as KiwiMaru from '@remotion/google-fonts/KiwiMaru';
import * as KleeOne from '@remotion/google-fonts/KleeOne';
import * as YuseiMagic from '@remotion/google-fonts/YuseiMagic';
import * as Yomogi from '@remotion/google-fonts/Yomogi';
import * as ZenKurenaido from '@remotion/google-fonts/ZenKurenaido';
import * as HachiMaruPop from '@remotion/google-fonts/HachiMaruPop';
import * as YujiBoku from '@remotion/google-fonts/YujiBoku';
import * as YujiMai from '@remotion/google-fonts/YujiMai';
import * as YujiSyuku from '@remotion/google-fonts/YujiSyuku';
import * as DelaGothicOne from '@remotion/google-fonts/DelaGothicOne';
import * as ReggaeOne from '@remotion/google-fonts/ReggaeOne';
import * as MochiyPopPOne from '@remotion/google-fonts/MochiyPopPOne';
import * as RocknRollOne from '@remotion/google-fonts/RocknRollOne';
import * as RampartOne from '@remotion/google-fonts/RampartOne';
import * as TrainOne from '@remotion/google-fonts/TrainOne';
import * as PottaOne from '@remotion/google-fonts/PottaOne';
import * as Stick from '@remotion/google-fonts/Stick';
import * as DotGothic16 from '@remotion/google-fonts/DotGothic16';
import * as WDXLLubrifontJPN from '@remotion/google-fonts/WDXLLubrifontJPN';
import * as KaiseiDecol from '@remotion/google-fonts/KaiseiDecol';
import * as KaiseiHarunoUmi from '@remotion/google-fonts/KaiseiHarunoUmi';
import * as KaiseiOpti from '@remotion/google-fonts/KaiseiOpti';
import * as KaiseiTokumin from '@remotion/google-fonts/KaiseiTokumin';
import * as Anton from '@remotion/google-fonts/Anton';
import * as BebasNeue from '@remotion/google-fonts/BebasNeue';

import {
  buildFontStack,
  resolveCaptionFontFamily,
  resolveCaptionFontWeight,
  type CaptionFontFamilyName,
  type CaptionFontWeightInput,
} from '../fonts/captionFonts';

// 各モジュールの loadFont はフォントごとにウェイト/サブセットのリテラル型が違うため、
// 共通の緩い型に揃えて扱う (渡す値は CAPTION_FONT_WEIGHTS と getInfo().subsets から作る)。
type GoogleFontModule = {
  getInfo: () => { fontFamily: string; subsets: string[] };
  loadFont: (
    style?: 'normal',
    options?: { weights?: string[]; subsets?: string[]; ignoreTooManyRequestsWarning?: boolean },
  ) => { fontFamily: string; waitUntilDone: () => Promise<void> };
};

const FONT_MODULES: Record<CaptionFontFamilyName, GoogleFontModule> = {
  // ゴシック
  'Noto Sans JP': NotoSansJP as unknown as GoogleFontModule,
  'Zen Kaku Gothic New': ZenKakuGothicNew as unknown as GoogleFontModule,
  'Zen Kaku Gothic Antique': ZenKakuGothicAntique as unknown as GoogleFontModule,
  'IBM Plex Sans JP': IBMPlexSansJP as unknown as GoogleFontModule,
  'LINE Seed JP': LINESeedJP as unknown as GoogleFontModule,
  'M PLUS 2': MPLUS2 as unknown as GoogleFontModule,
  'M PLUS 1p': MPLUS1p as unknown as GoogleFontModule,
  'M PLUS U': MPLUSU as unknown as GoogleFontModule,
  'M PLUS 1 Code': MPLUS1Code as unknown as GoogleFontModule,
  'BIZ UDPGothic': BIZUDPGothic as unknown as GoogleFontModule,
  'Sawarabi Gothic': SawarabiGothic as unknown as GoogleFontModule,
  Kosugi: Kosugi as unknown as GoogleFontModule,
  // 明朝
  'Noto Serif JP': NotoSerifJP as unknown as GoogleFontModule,
  'Shippori Mincho': ShipporiMincho as unknown as GoogleFontModule,
  'Shippori Mincho B1': ShipporiMinchoB1 as unknown as GoogleFontModule,
  'Zen Old Mincho': ZenOldMincho as unknown as GoogleFontModule,
  'Zen Antique': ZenAntique as unknown as GoogleFontModule,
  'Zen Antique Soft': ZenAntiqueSoft as unknown as GoogleFontModule,
  'Shippori Antique': ShipporiAntique as unknown as GoogleFontModule,
  'Shippori Antique B1': ShipporiAntiqueB1 as unknown as GoogleFontModule,
  'BIZ UDPMincho': BIZUDPMincho as unknown as GoogleFontModule,
  'Sawarabi Mincho': SawarabiMincho as unknown as GoogleFontModule,
  'Hina Mincho': HinaMincho as unknown as GoogleFontModule,
  'New Tegomin': NewTegomin as unknown as GoogleFontModule,
  // 丸ゴシック
  'M PLUS Rounded 1c': MPLUSRounded1c as unknown as GoogleFontModule,
  'Zen Maru Gothic': ZenMaruGothic as unknown as GoogleFontModule,
  'Kosugi Maru': KosugiMaru as unknown as GoogleFontModule,
  'Kiwi Maru': KiwiMaru as unknown as GoogleFontModule,
  // 手書き
  'Klee One': KleeOne as unknown as GoogleFontModule,
  'Yusei Magic': YuseiMagic as unknown as GoogleFontModule,
  Yomogi: Yomogi as unknown as GoogleFontModule,
  'Zen Kurenaido': ZenKurenaido as unknown as GoogleFontModule,
  'Hachi Maru Pop': HachiMaruPop as unknown as GoogleFontModule,
  'Yuji Boku': YujiBoku as unknown as GoogleFontModule,
  'Yuji Mai': YujiMai as unknown as GoogleFontModule,
  'Yuji Syuku': YujiSyuku as unknown as GoogleFontModule,
  // デザイン
  'Dela Gothic One': DelaGothicOne as unknown as GoogleFontModule,
  'Reggae One': ReggaeOne as unknown as GoogleFontModule,
  'Mochiy Pop P One': MochiyPopPOne as unknown as GoogleFontModule,
  'RocknRoll One': RocknRollOne as unknown as GoogleFontModule,
  'Rampart One': RampartOne as unknown as GoogleFontModule,
  'Train One': TrainOne as unknown as GoogleFontModule,
  'Potta One': PottaOne as unknown as GoogleFontModule,
  Stick: Stick as unknown as GoogleFontModule,
  DotGothic16: DotGothic16 as unknown as GoogleFontModule,
  'WDXL Lubrifont JP N': WDXLLubrifontJPN as unknown as GoogleFontModule,
  'Kaisei Decol': KaiseiDecol as unknown as GoogleFontModule,
  'Kaisei HarunoUmi': KaiseiHarunoUmi as unknown as GoogleFontModule,
  'Kaisei Opti': KaiseiOpti as unknown as GoogleFontModule,
  'Kaisei Tokumin': KaiseiTokumin as unknown as GoogleFontModule,
  // 欧文
  Anton: Anton as unknown as GoogleFontModule,
  'Bebas Neue': BebasNeue as unknown as GoogleFontModule,
};

// 日本語テロップに必要なサブセットだけ読み込む (欧文フォントは japanese を持たないので latin のみ)。
const WANTED_SUBSETS = ['japanese', 'latin'];

export interface LoadedCaptionFont {
  /** CSS の font-family スタック (先頭が読み込んだ Web フォントの登録名) */
  fontFamily: string;
  /** CSS の font-weight (そのフォントが提供するウェイトに丸めた値) */
  fontWeight: number;
}

/**
 * design.fontFamily / fontWeight に対応する Web フォントを読み込み、CSS に渡す値を返す。
 * 描画中に呼んでよい (内部キャッシュ + delayRender)。
 */
export function loadCaptionFont(family: string, weight: CaptionFontWeightInput): LoadedCaptionFont {
  const name = resolveCaptionFontFamily(family);
  const module = FONT_MODULES[name];
  const resolvedWeight = resolveCaptionFontWeight(name, weight);
  const subsets = module.getInfo().subsets.filter((s) => WANTED_SUBSETS.includes(s));
  // 使うウェイトだけ読み込む (loadFont はウェイト単位でキャッシュされる)。
  const loaded = module.loadFont('normal', {
    weights: [String(resolvedWeight)],
    subsets,
    // 日本語フォントは Google 側で unicode-range ごとに ~120 ファイルに分割されているため、
    // 1 ウェイトでも 100 件超のリクエストになる (必要な範囲だけ読む仕組みなので想定どおり)。
    ignoreTooManyRequestsWarning: true,
  });
  return {
    fontFamily: buildFontStack(loaded.fontFamily),
    fontWeight: resolvedWeight,
  };
}

/**
 * loadCaptionFont の非同期版。フォントファイルの取得完了 (waitUntilDone) まで待ってから返すので、
 * Remotion の外 (canvas でのテロップ画像化など、delayRender が効かない場所) で使う。
 */
export async function loadCaptionFontAsync(
  family: string,
  weight: CaptionFontWeightInput,
): Promise<LoadedCaptionFont> {
  const name = resolveCaptionFontFamily(family);
  const module = FONT_MODULES[name];
  const resolvedWeight = resolveCaptionFontWeight(name, weight);
  const subsets = module.getInfo().subsets.filter((s) => WANTED_SUBSETS.includes(s));
  const loaded = module.loadFont('normal', {
    weights: [String(resolvedWeight)],
    subsets,
    ignoreTooManyRequestsWarning: true,
  });
  await loaded.waitUntilDone();
  return {
    fontFamily: buildFontStack(loaded.fontFamily),
    fontWeight: resolvedWeight,
  };
}
